import { ReactNode, useMemo, useState } from 'react'
import {
    FaBirthdayCake,
    FaChevronDown,
    FaDatabase,
    FaEnvelope,
    FaExternalLinkAlt,
    FaPaperPlane,
    FaPhone,
    FaSearchDollar,
    FaSyncAlt,
    FaTools,
} from 'react-icons/fa'
import {
    Avatar,
    Banner,
    Button,
    Card,
    CardGrid,
    Column,
    DataTable,
    KeyValueList,
    Spinner,
    StatCard,
    StatGrid,
    StatusPill,
    cx,
} from '../../../../../ui'
import { Claim, ClientData, formatDate, getFormattedAddress, getFullName } from '../shared'
import { SignatureEvidenceCard, SignatureEvidenceState } from './signatureEvidenceCard'
import { DocumentsCard, DocumentsState } from './documentsCard'
import { ProvenancePill } from './provenancePill'
import { ClaimDetailPanel } from './claimDetail'
import s from './styles.module.scss'

export interface ClientRecordProps {
    client: ClientData
    canCancelClaims: boolean
    creditReportUrl: string | null
    isLoadingCreditReport: boolean
    /** Freshly regenerated base CFA, if the operator has just run that action. */
    regeneratedCfaUrl: string | null
    isSendingEsign: boolean
    isRegeneratingCfa: boolean
    isFindingPcpAgreements: boolean
    cancellingCaseRef: string | null
    /** Audit trail for the signed CFA; only rendered when `client.cfa_signed`. */
    signatureEvidence: SignatureEvidenceState
    onRetrySignatureEvidence: () => void
    /** Every document on file in S3 for this client. */
    documents: DocumentsState
    onRetryDocuments: () => void
    /** Contact notes, rendered between the claims and the signature evidence. */
    notesSection?: ReactNode
    /** Scrolls to (and opens) the Account actions section; omitted when the user cannot see it. */
    onJumpToActions?: () => void
    onSendEsignLink: () => void
    onRegenerateCfa: () => void
    onOpenCreditReportForm: () => void
    onFindPcpAgreements: () => void
    onCancelClaim: (caseRef: string) => void
}

const ExternalLink = ({ href, children }: { href: string; children: ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className={s.link}>
        {children} <FaExternalLinkAlt aria-hidden="true" />
    </a>
)

const claimIsPending = (claim: Claim) => !claim.case_ref || claim.case_ref === 'TBC'

/** The selected client: identity header, headline figures, then detail panels. */
export const ClientRecord = ({
    client,
    canCancelClaims,
    creditReportUrl,
    isLoadingCreditReport,
    regeneratedCfaUrl,
    isSendingEsign,
    isRegeneratingCfa,
    isFindingPcpAgreements,
    cancellingCaseRef,
    signatureEvidence,
    onRetrySignatureEvidence,
    documents,
    onRetryDocuments,
    notesSection,
    onJumpToActions,
    onSendEsignLink,
    onRegenerateCfa,
    onOpenCreditReportForm,
    onFindPcpAgreements,
    onCancelClaim,
}: ClientRecordProps) => {
    const name = getFullName(client)
    const claims = useMemo(() => client.claims ?? [], [client.claims])
    const activeClaims = useMemo(() => claims.filter((c) => !c.cancelled), [claims])
    const cancelledClaims = claims.length - activeClaims.length
    const signedCfaLink = client.cfa_signed ? claims.find((c) => c.cfa_link)?.cfa_link : undefined
    const address = getFormattedAddress(client.current_address)
    const provenance = client.provenance
    // Claims whose detail panel is open (keyed by case ref); reset when the client changes.
    const [expandedClaims, setExpandedClaims] = useState<string[]>([])
    const claimKey = (c: Claim) => c.case_ref || 'TBC'
    const toggleClaim = (c: Claim) => {
        if (claimIsPending(c)) return
        const key = claimKey(c)
        setExpandedClaims((open) => (open.includes(key) ? open.filter((k) => k !== key) : [...open, key]))
    }

    const claimColumns: Column<Claim>[] = [
        {
            key: 'expand',
            header: '',
            width: 32,
            nowrap: true,
            render: (c) =>
                claimIsPending(c) ? null : (
                    <span
                        className={cx(s.claimChevron, expandedClaims.includes(claimKey(c)) && s.claimChevronOpen)}
                        aria-hidden="true"
                    >
                        <FaChevronDown />
                    </span>
                ),
        },
        {
            key: 'case_ref',
            header: 'Case ref',
            nowrap: true,
            render: (c) => <span className={s.mono}>{c.case_ref || 'TBC'}</span>,
        },
        { key: 'lender', header: 'Lender', render: (c) => c.lender || '—' },
        {
            key: 'policy_number',
            header: 'Agreement',
            render: (c) =>
                c.policy_number ? (
                    <span className={s.mono}>{c.policy_number}</span>
                ) : (
                    <span className={s.muted}>Pending claim discovery</span>
                ),
        },
        {
            key: 'start_date',
            header: 'Start date',
            nowrap: true,
            render: (c) => (c.start_date && c.policy_number ? formatDate(c.start_date) : '—'),
        },
        { key: 'length_of_contract', header: 'Term', nowrap: true, render: (c) => c.length_of_contract || '—' },
        {
            key: 'status',
            header: 'Status',
            nowrap: true,
            render: (c) =>
                c.cancelled ? (
                    <StatusPill tone="bad" dot>
                        Cancelled{c.cancellation_date ? ` · ${formatDate(c.cancellation_date)}` : ''}
                    </StatusPill>
                ) : (
                    <StatusPill tone="good" dot>
                        Active
                    </StatusPill>
                ),
        },
    ]

    if (canCancelClaims) {
        claimColumns.push({
            key: 'actions',
            header: '',
            align: 'right',
            nowrap: true,
            render: (c) =>
                c.cancelled ? null : (
                    <Button
                        size="sm"
                        variant="danger"
                        disabled={claimIsPending(c)}
                        loading={cancellingCaseRef === c.case_ref}
                        title={claimIsPending(c) ? 'Available once claim discovery is complete.' : undefined}
                        onClick={(e) => {
                            e.stopPropagation()
                            onCancelClaim(c.case_ref)
                        }}
                    >
                        Cancel claim
                    </Button>
                ),
        })
    }

    const creditReportPill = isLoadingCreditReport ? (
        <StatusPill>Checking…</StatusPill>
    ) : creditReportUrl ? (
        <StatusPill tone="good" dot>
            Available
        </StatusPill>
    ) : (
        <StatusPill tone="warn" dot>
            Not run
        </StatusPill>
    )

    return (
        <div className={s.record}>
            {/* Identity ------------------------------------------------------ */}
            <Card accentTop>
                <div className={s.heroRow}>
                    <Avatar name={name} />
                    <div className={s.heroText}>
                        <h2 className={s.heroName}>{name}</h2>
                        <div className={s.heroMeta}>
                            <span>
                                <FaEnvelope aria-hidden="true" /> {client.email || '—'}
                            </span>
                            {client.telephone && (
                                <span>
                                    <FaPhone aria-hidden="true" /> {client.telephone}
                                </span>
                            )}
                            <span>
                                <FaBirthdayCake aria-hidden="true" /> {formatDate(client.dob)}
                            </span>
                            <span title="Database this record is held in">
                                <FaDatabase aria-hidden="true" /> {client.origin}
                            </span>
                        </div>
                    </div>
                    <div className={s.heroPills}>
                        <ProvenancePill provenance={provenance} fallback={client.origin} />
                        {client.cfa_signed ? (
                            <StatusPill tone="good" dot>
                                CFA signed
                            </StatusPill>
                        ) : (
                            <StatusPill tone="warn" dot>
                                Awaiting signature
                            </StatusPill>
                        )}
                        {client.has_blocked_claims && (
                            <StatusPill tone="bad" dot>
                                Blocked claims
                            </StatusPill>
                        )}
                    </div>
                    {(onJumpToActions || (!client.cfa_signed && client.esign_link)) && (
                        <div className={s.heroActions}>
                            {!client.cfa_signed && client.esign_link && (
                                <Button icon={<FaPaperPlane />} loading={isSendingEsign} onClick={onSendEsignLink}>
                                    Send e-sign link
                                </Button>
                            )}
                            {onJumpToActions && (
                                <Button
                                    variant="neutral"
                                    icon={<FaTools />}
                                    onClick={onJumpToActions}
                                    title="Jump to account actions"
                                >
                                    Actions
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </Card>

            {/* Headline figures ----------------------------------------------- */}
            <StatGrid minColumn={160}>
                <StatCard compact label="Claims" value={claims.length} />
                <StatCard
                    compact
                    label="Active claims"
                    value={activeClaims.length}
                    tone={activeClaims.length ? 'good' : 'muted'}
                />
                <StatCard compact label="Cancelled" value={cancelledClaims} tone={cancelledClaims ? 'bad' : 'muted'} />
                <StatCard
                    compact
                    format={false}
                    label="CFA"
                    value={client.cfa_signed ? 'Signed' : 'Unsigned'}
                    tone={client.cfa_signed ? 'good' : 'warn'}
                    sub={client.cfa_signed && client.cfa_signed_date ? formatDate(client.cfa_signed_date) : undefined}
                />
                <StatCard
                    compact
                    format={false}
                    label="Credit report"
                    value={isLoadingCreditReport ? <Spinner /> : creditReportUrl ? 'Available' : 'Not run'}
                    tone={isLoadingCreditReport ? 'default' : creditReportUrl ? 'good' : 'muted'}
                />
            </StatGrid>

            {/* Detail panels -------------------------------------------------- */}
            <CardGrid minColumn={380}>
                <Card
                    title="Client details"
                    subtitle={`As recorded in the ${client.origin} database`}
                    actions={<ProvenancePill provenance={provenance} fallback={client.origin} size="sm" />}
                >
                    <KeyValueList
                        items={[
                            {
                                label: 'Source',
                                value: provenance ? (
                                    <span className={s.provenance}>
                                        <span>
                                            {provenance.legacy
                                                ? `${provenance.system} (legacy system)`
                                                : provenance.system}
                                        </span>
                                        {provenance.detail && (
                                            <span className={s.provenanceDetail}>{provenance.detail}</span>
                                        )}
                                    </span>
                                ) : (
                                    client.origin
                                ),
                            },
                            { label: 'Database', value: client.origin },
                            {
                                label: 'Record origin',
                                value: provenance?.record_origin ?? null,
                                mono: true,
                            },
                            { label: 'Client ID', value: client.client_id, mono: true },
                            { label: 'Full name', value: name },
                            { label: 'Date of birth', value: formatDate(client.dob) },
                            { label: 'Email', value: client.email },
                            { label: 'Telephone', value: client.telephone },
                            { label: 'Address', value: address },
                        ]}
                    />
                </Card>

                <Card
                    title="CFA & signing"
                    actions={
                        client.cfa_signed ? (
                            <StatusPill tone="good" dot>
                                Signed
                            </StatusPill>
                        ) : (
                            <StatusPill tone="warn" dot>
                                Unsigned
                            </StatusPill>
                        )
                    }
                    footer={
                        <div className={s.cardActions}>
                            {!client.cfa_signed && client.esign_link && (
                                <Button
                                    size="sm"
                                    icon={<FaPaperPlane />}
                                    loading={isSendingEsign}
                                    onClick={onSendEsignLink}
                                >
                                    Send e-sign link
                                </Button>
                            )}
                            <Button
                                size="sm"
                                icon={<FaSyncAlt />}
                                loading={isRegeneratingCfa}
                                onClick={onRegenerateCfa}
                            >
                                Regenerate base CFA
                            </Button>
                        </div>
                    }
                >
                    <div className={s.stackSm}>
                        {client.has_blocked_claims && (
                            <Banner tone="bad" title="Blocked claims">
                                One or more of this client's claims are blocked.
                            </Banner>
                        )}
                        {regeneratedCfaUrl && (
                            <Banner
                                tone="good"
                                title="Base CFA regenerated"
                                actions={
                                    <Button
                                        as="a"
                                        size="sm"
                                        href={regeneratedCfaUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View
                                    </Button>
                                }
                            >
                                The new document is ready.
                            </Banner>
                        )}
                        <KeyValueList
                            items={[
                                {
                                    label: 'Signed on',
                                    value:
                                        client.cfa_signed && client.cfa_signed_date
                                            ? formatDate(client.cfa_signed_date)
                                            : null,
                                },
                                {
                                    label: 'Initial CFA',
                                    value: client.base_cfa_link ? (
                                        <ExternalLink href={client.base_cfa_link}>View document</ExternalLink>
                                    ) : null,
                                },
                                ...(client.cfa_signed
                                    ? [
                                          {
                                              label: 'Signed CFA',
                                              value: signedCfaLink ? (
                                                  <ExternalLink href={signedCfaLink}>View document</ExternalLink>
                                              ) : null,
                                          },
                                      ]
                                    : [
                                          {
                                              label: 'E-sign link',
                                              value: client.esign_link ? (
                                                  <ExternalLink href={client.esign_link}>
                                                      Open signing page
                                                  </ExternalLink>
                                              ) : null,
                                          },
                                      ]),
                            ]}
                        />
                    </div>
                </Card>

                <Card
                    title="Credit report"
                    actions={creditReportPill}
                    footer={
                        <div className={s.cardActions}>
                            {creditReportUrl && (
                                <Button
                                    as="a"
                                    size="sm"
                                    href={creditReportUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    icon={<FaExternalLinkAlt />}
                                >
                                    View report
                                </Button>
                            )}
                            <Button
                                size="sm"
                                icon={<FaSyncAlt />}
                                onClick={onOpenCreditReportForm}
                                disabled={isLoadingCreditReport}
                            >
                                {creditReportUrl ? 'Re-run report' : 'Run report'}
                            </Button>
                            <Button
                                size="sm"
                                icon={<FaSearchDollar />}
                                loading={isFindingPcpAgreements}
                                onClick={onFindPcpAgreements}
                            >
                                Find PCP agreements
                            </Button>
                        </div>
                    }
                >
                    {isLoadingCreditReport ? (
                        <Spinner text="Checking for an existing report…" />
                    ) : (
                        <p className={s.cardText}>
                            {creditReportUrl
                                ? 'A credit report is on file for this client. Re-run it to refresh the data, or scan it for PCP agreements.'
                                : 'No credit report has been run for this client yet. Confirm their details and run one to begin claim discovery.'}
                        </p>
                    )}
                </Card>
            </CardGrid>

            {/* Claims ----------------------------------------------------------- */}
            <Card
                title="Claims"
                subtitle="Agreements identified for this client. Select a claim to see its full detail."
                padding="none"
                actions={
                    <StatusPill round tone={claims.length ? 'accent' : 'neutral'}>
                        {claims.length}
                    </StatusPill>
                }
            >
                <DataTable
                    bare
                    dense
                    columns={claimColumns}
                    rows={claims}
                    rowKey={claimKey}
                    rowTone={(c) => (c.cancelled ? 'bad' : undefined)}
                    onRowClick={toggleClaim}
                    expandedKeys={expandedClaims}
                    renderExpanded={(c) => <ClaimDetailPanel origin={client.origin} caseRef={c.case_ref} />}
                    emptyTitle="No claims yet"
                    emptyDescription="Claims appear once claim discovery completes after the CFA is signed."
                />
            </Card>

            {/* Contact notes ------------------------------------------------- */}
            {notesSection}

            {/* Signature evidence ---------------------------------------------- */}
            {client.cfa_signed && (
                <SignatureEvidenceCard clientName={name} state={signatureEvidence} onRetry={onRetrySignatureEvidence} />
            )}

            {/* Documents --------------------------------------------------------- */}
            <DocumentsCard
                origin={client.origin}
                canSendLoc={canCancelClaims}
                state={documents}
                onRetry={onRetryDocuments}
            />
        </div>
    )
}
